import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { FaEnvelope, FaBell, FaEye, FaSave, FaWhatsapp, FaPhone, FaMapMarkerAlt, FaFacebookF, FaInstagram, FaTwitter, FaYoutube } from 'react-icons/fa';
import { apiService } from '../services/api';
import { useSiteSettings } from '../context/SiteSettingsContext';
import {
  PUBLIC_NAV_MENU,
  buildVisibilityFromApi,
  navKeyToDbKey,
  allNavVisibilityKeys,
} from '../config/publicNavigation';

function Settings() {
  const { refresh } = useSiteSettings();
  const [userRole] = useState(() => localStorage.getItem('userRole') || '');
  const isDeveloper = userRole === 'Developer';
  const [loading, setLoading] = useState(false);
  const [emailConfig, setEmailConfig] = useState({
    EMAIL_USER: '',
    EMAIL_PASS: '',
    SCHOOL_EMAIL: ''
  });

  const [menuVisibility, setMenuVisibility] = useState(() => buildVisibilityFromApi({}));

  const [whatsappConfig, setWhatsappConfig] = useState({
    WHATSAPP_NUMBER: '919034851777',
    WHATSAPP_ENABLED: true
  });

  const [schoolInfo, setSchoolInfo] = useState({
    SCHOOL_NAME: 'Vidya Vatika World School',
    SCHOOL_TAGLINE: 'Premier CBSE Institution in Panipat',
    SCHOOL_HERO_SUBLINE: 'Nurturing Excellence, Values & Global Readiness',
    SCHOOL_LOGO_URL: '/images/logo.png',
    SCHOOL_PHONE: '9034851777',
    SCHOOL_PHONE_ALT: '8950105177',
    SCHOOL_LANDLINE: '0180-4017777',
    SCHOOL_EMAIL_INFO: 'info@vidyavatikaworldschool.com',
    SCHOOL_ADDRESS: 'G.T. Road, Near Railway Crossing, Panipat, Haryana 132103',
    ADMISSIONS_SESSION_LABEL: '',
    SHOW_LEADERSHIP_MESSAGE: true,
    LEADERSHIP_MEMBERS: [],
    SHOW_ACHIEVEMENTS_SECTION: true,
    ACHIEVEMENTS_ITEMS: [
      { icon: '🥇', title: 'CBSE Top Ranked', subtitle: '2023-24', isActive: true },
      { icon: '🌟', title: 'Best School Award', subtitle: 'Panipat', isActive: true },
      { icon: '🎯', title: '100% Result', subtitle: 'Class 10 & 12', isActive: true },
      { icon: '🏅', title: 'Sports Excellence', subtitle: 'State Level', isActive: true },
      { icon: '💡', title: 'Innovation Hub', subtitle: 'STEM Education', isActive: true },
      { icon: '✅', title: 'ISO Certified', subtitle: 'Quality Standard', isActive: true },
    ],
    SHOW_HOME_STATS: true,
    HOME_STATS_ITEMS: [
      { key: 'students', number: 1000, suffix: '+', label: 'Students', isActive: true },
      { key: 'teachers', number: 50, suffix: '+', label: 'Teachers', isActive: true },
      { key: 'years', number: 20, suffix: '+', label: 'Years Experience', isActive: true },
      { key: 'result', number: 100, suffix: '%', label: 'Result Focus', isActive: true },
    ],
    DIRECTOR_NAME: 'Krishan Kumar Aggarwal',
    DIRECTOR_MESSAGE: '',
    DIRECTOR_IMAGE_URL: '/images/director.png',
    PRINCIPAL_NAME: 'Suman Sharma',
    PRINCIPAL_MESSAGE: '',
    PRINCIPAL_IMAGE_URL: '/images/principal.png',
    GOOGLE_MAP_EMBED_URL: '',
  });

  const [socialLinks, setSocialLinks] = useState({
    SOCIAL_FACEBOOK: 'https://www.facebook.com/vidyavatikaworld',
    SOCIAL_INSTAGRAM: 'https://www.instagram.com/vidyavatikaworld',
    SOCIAL_TWITTER: 'https://x.com/VidyaVatikaPNP',
    SOCIAL_YOUTUBE: ''
  });

  // Load all settings from database
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await apiService.settings.getAll();
      
      if (response.data) {
        const settings = response.data;
        
        // Load email config
        if (settings.email) {
          setEmailConfig(prev => ({ ...prev, ...settings.email }));
        }
        
        if (settings.menu) {
          setMenuVisibility(buildVisibilityFromApi(settings.menu));
        }
        
        // Load WhatsApp config
        if (settings.whatsapp) {
          setWhatsappConfig(prev => ({ ...prev, ...settings.whatsapp }));
        }
        
        // Load school info
        if (settings.school) {
          const next = { ...settings.school };
          if (typeof next.LEADERSHIP_MEMBERS === 'string') {
            try {
              const parsed = JSON.parse(next.LEADERSHIP_MEMBERS);
              if (Array.isArray(parsed)) next.LEADERSHIP_MEMBERS = parsed;
            } catch (e) {
              // ignore
            }
          }
          if (typeof next.ACHIEVEMENTS_ITEMS === 'string') {
            try {
              const parsed = JSON.parse(next.ACHIEVEMENTS_ITEMS);
              if (Array.isArray(parsed)) next.ACHIEVEMENTS_ITEMS = parsed;
            } catch (e) {
              // ignore
            }
          }
          if (typeof next.HOME_STATS_ITEMS === 'string') {
            try {
              const parsed = JSON.parse(next.HOME_STATS_ITEMS);
              if (Array.isArray(parsed)) next.HOME_STATS_ITEMS = parsed;
            } catch (e) {
              // ignore
            }
          }
          setSchoolInfo((prev) => ({ ...prev, ...next }));
        }
        
        // Load social links
        if (settings.social) {
          setSocialLinks(prev => ({ ...prev, ...settings.social }));
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmailConfig({ ...emailConfig, [e.target.name]: e.target.value });
  };

  const handleMenuToggle = (key, childKeys = []) => {
    setMenuVisibility((prev) => {
      const nextVisible = prev[key] === false;
      const next = { ...prev, [key]: nextVisible };
      if (!nextVisible && childKeys.length) {
        childKeys.forEach((childKey) => {
          next[childKey] = false;
        });
      }
      return next;
    });
  };

  const handleWhatsappChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setWhatsappConfig({ ...whatsappConfig, [e.target.name]: value });
  };

  const handleSchoolInfoChange = (e) => {
    setSchoolInfo({ ...schoolInfo, [e.target.name]: e.target.value });
  };

  const handleSocialChange = (e) => {
    setSocialLinks({ ...socialLinks, [e.target.name]: e.target.value });
  };

  const handleSaveEmail = async () => {
    try {
      setLoading(true);
      await Promise.all([
        apiService.settings.update('EMAIL_USER', { 
          value: emailConfig.EMAIL_USER, 
          category: 'email', 
          description: 'Gmail address for sending emails' 
        }),
        apiService.settings.update('EMAIL_PASS', { 
          value: emailConfig.EMAIL_PASS, 
          category: 'email', 
          description: 'Gmail App Password' 
        }),
        apiService.settings.update('SCHOOL_EMAIL', { 
          value: emailConfig.SCHOOL_EMAIL, 
          category: 'email', 
          description: 'School email to receive notifications' 
        })
      ]);
      alert('✅ Email configuration saved! Emails will now be sent using these settings.');
    } catch (error) {
      console.error('Error saving email config:', error);
      alert('❌ Error saving email configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMenu = async () => {
    try {
      setLoading(true);
      const updates = allNavVisibilityKeys().flatMap((key) => {
        const payload = {
          value: menuVisibility[key] !== false,
          category: 'menu',
          description: `Show ${key} in navigation`,
        };
        if (key === 'academics') {
          return [
            apiService.settings.update(navKeyToDbKey(key), payload),
            apiService.settings.update('MENU_ACADAMICS', payload),
          ];
        }
        return [apiService.settings.update(navKeyToDbKey(key), payload)];
      });
      await Promise.all(updates);
      window.dispatchEvent(new Event('site-settings:refresh'));
      alert('✅ Menu visibility settings saved! Changes will reflect immediately on the website.');
    } catch (error) {
      console.error('Error saving menu config:', error);
      alert('❌ Error saving menu settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWhatsapp = async () => {
    try {
      setLoading(true);
      await Promise.all([
        apiService.settings.update('WHATSAPP_NUMBER', { 
          value: whatsappConfig.WHATSAPP_NUMBER, 
          category: 'whatsapp', 
          description: 'WhatsApp number for chat integration' 
        }),
        apiService.settings.update('WHATSAPP_ENABLED', { 
          value: whatsappConfig.WHATSAPP_ENABLED, 
          category: 'whatsapp', 
          description: 'Enable/disable WhatsApp chat button' 
        })
      ]);
      alert('✅ WhatsApp configuration saved! Chat button updated.');
      try {
        await refresh();
      } catch (e) {
        window.dispatchEvent(new Event('site-settings:refresh'));
      }
    } catch (error) {
      console.error('Error saving whatsapp config:', error);
      alert('❌ Error saving WhatsApp configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchoolInfo = async () => {
    try {
      setLoading(true);
      const updates = Object.entries(schoolInfo).map(([key, value]) => 
        apiService.settings.update(key, { 
          value: value, 
          category: 'school', 
          description: getSchoolInfoDescription(key) 
        })
      );
      await Promise.all(updates);
      alert('✅ School information saved!');
      try {
        await refresh();
      } catch (e) {
        window.dispatchEvent(new Event('site-settings:refresh'));
      }
    } catch (error) {
      console.error('Error saving school info:', error);
      alert('❌ Error saving school information');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSocial = async () => {
    try {
      setLoading(true);
      const updates = Object.entries(socialLinks).map(([key, value]) => 
        apiService.settings.update(key, { 
          value: value, 
          category: 'social', 
          description: `${key.replace('SOCIAL_', '')} profile URL` 
        })
      );
      await Promise.all(updates);
      alert('✅ Social media links saved!');
      try {
        await refresh();
      } catch (e) {
        window.dispatchEvent(new Event('site-settings:refresh'));
      }
    } catch (error) {
      console.error('Error saving social links:', error);
      alert('❌ Error saving social media links');
    } finally {
      setLoading(false);
    }
  };

  const getSchoolInfoDescription = (key) => {
    const descriptions = {
      SCHOOL_NAME: 'Official school name',
      SCHOOL_TAGLINE: 'Short line under name (header & footer)',
      SCHOOL_HERO_SUBLINE: 'Extra line on home hero section',
      SCHOOL_LOGO_URL: 'Logo image path or URL (/images/logo.png or uploads/...)',
      SCHOOL_PHONE: 'School contact number',
      SCHOOL_PHONE_ALT: 'Alternate phone number (optional)',
      SCHOOL_LANDLINE: 'Landline number (optional)',
      SCHOOL_EMAIL_INFO: 'General inquiry email (shown on site)',
      SCHOOL_ADDRESS: 'School address',
      ADMISSIONS_SESSION_LABEL: 'Admissions session label (e.g. 2026-27). Empty = auto',
      SHOW_LEADERSHIP_MESSAGE: 'Show/hide Leadership Message section on website',
      LEADERSHIP_MEMBERS: 'Leadership members list (advanced)',
      SHOW_ACHIEVEMENTS_SECTION: 'Show/hide Achievements section on Home page',
      ACHIEVEMENTS_ITEMS: 'Achievements items list (advanced)',
      SHOW_HOME_STATS: 'Show/hide Home stats counters section',
      HOME_STATS_ITEMS: 'Home stats counters list',
      DIRECTOR_NAME: 'Director name (Leadership Message section)',
      DIRECTOR_MESSAGE: 'Director message (Leadership Message section)',
      DIRECTOR_IMAGE_URL: 'Director image path/URL (/images/... or uploads/...)',
      PRINCIPAL_NAME: 'Principal name (Leadership Message section)',
      PRINCIPAL_MESSAGE: 'Principal message (Leadership Message section)',
      PRINCIPAL_IMAGE_URL: 'Principal image path/URL (/images/... or uploads/...)',
      GOOGLE_MAP_EMBED_URL: 'Google Maps embed URL (iframe src from Share → Embed a map)',
    };
    return descriptions[key] || '';
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="mb-8">
          <h1 className="text-3xl font-bold text-blue-900 mb-2">Settings</h1>
          <p className="text-gray-600">Manage email, WhatsApp, menu visibility, school info & social media</p>
        </motion.div>

        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
              <p className="mt-4 text-gray-600">Saving...</p>
            </div>
          </div>
        )}

        {/* WhatsApp Configuration */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-green-100 p-3 rounded-full">
              <FaWhatsapp className="text-2xl text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-blue-900">WhatsApp Configuration</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                WhatsApp Number (with country code)
              </label>
              <input
                type="text"
                name="WHATSAPP_NUMBER"
                value={whatsappConfig.WHATSAPP_NUMBER}
                onChange={handleWhatsappChange}
                placeholder="919034851777"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Format: Country code + number (e.g., 919034851777 for India)</p>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-semibold text-gray-800">Enable WhatsApp Chat</p>
                <p className="text-xs text-gray-500">Show/hide WhatsApp button on website</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="WHATSAPP_ENABLED"
                  checked={whatsappConfig.WHATSAPP_ENABLED}
                  onChange={handleWhatsappChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
              </label>
            </div>

            <button
              onClick={handleSaveWhatsapp}
              className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 rounded-lg hover:from-green-700 hover:to-green-800 transition font-semibold flex items-center justify-center gap-2"
            >
              <FaWhatsapp /> Save WhatsApp Settings
            </button>
          </div>
        </motion.div>

        {/* Email Configuration — SMTP: Developer only */}
        {isDeveloper ? (
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-3 rounded-full">
              <FaEnvelope className="text-2xl text-blue-900" />
            </div>
            <h2 className="text-2xl font-bold text-blue-900">Email Configuration (Developer)</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gmail Address (for sending emails)
              </label>
              <input
                type="email"
                name="EMAIL_USER"
                value={emailConfig.EMAIL_USER}
                onChange={handleEmailChange}
                placeholder="your-email@gmail.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">This email will send notifications for contact forms & admissions</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gmail App Password
              </label>
              <input
                type="password"
                name="EMAIL_PASS"
                value={emailConfig.EMAIL_PASS}
                onChange={handleEmailChange}
                placeholder="Your Gmail app password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Generate from Google Account → Security → 2-Step Verification → App Passwords
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                School Email (to receive notifications)
              </label>
              <input
                type="email"
                name="SCHOOL_EMAIL"
                value={emailConfig.SCHOOL_EMAIL}
                onChange={handleEmailChange}
                placeholder="principal@vidyavatika.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">All form submissions will be sent to this email</p>
            </div>

            <button
              onClick={handleSaveEmail}
              className="w-full bg-gradient-to-r from-blue-900 to-blue-800 text-white py-3 rounded-lg hover:from-blue-800 hover:to-blue-700 transition font-semibold flex items-center justify-center gap-2"
            >
              <FaSave /> Save Email Configuration
            </button>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 font-medium">📝 Setup Instructions:</p>
              <ol className="text-xs text-yellow-700 mt-2 space-y-1 list-decimal list-inside">
                <li>Create a file named <code>.env</code> in the <code>backend</code> folder</li>
                <li>Add these lines with your actual credentials:</li>
              </ol>
              <div className="bg-gray-100 p-3 rounded mt-2 font-mono text-xs">
                EMAIL_USER=your-email@gmail.com<br/>
                EMAIL_PASS=your-app-password<br/>
                SCHOOL_EMAIL=principal@vidyavatika.com
              </div>
            </div>
          </div>
        </motion.div>
        ) : (
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
          className="bg-gray-100 rounded-2xl border border-gray-200 p-6 mb-8">
            <p className="text-gray-700 text-sm">
              <strong>SMTP / Gmail app password</strong> sirf <strong>Developer</strong> account se change ho sakta hai taaki site ki mail setup galat na ho.
              Baaki settings (school name, phone, WhatsApp, menu, social) aap yahin se badal sakte hain.
            </p>
          </motion.div>
        )}

        {/* Menu Visibility */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-purple-100 p-3 rounded-full">
              <FaEye className="text-2xl text-purple-900" />
            </div>
            <h2 className="text-2xl font-bold text-blue-900">Menu Visibility</h2>
          </div>

          <p className="text-gray-600 mb-6">
            Main menu aur uske andar ke pages — sirf yahi items website header me dikhte hain. Parent band karne par poora section hide ho jata hai.
          </p>

          <div className="space-y-4">
            {PUBLIC_NAV_MENU.map((parent) => {
              const childKeys = (parent.submenu || []).map((s) => s.key);
              const parentVisible = menuVisibility[parent.key] !== false;
              return (
                <div key={parent.key} className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between p-4 bg-gray-50">
                    <div>
                      <p className="font-semibold text-gray-800">{parent.label}</p>
                      <p className="text-xs text-gray-500">
                        {parentVisible ? 'Visible' : 'Hidden'}
                        {childKeys.length > 0 ? ' · main menu item' : ''}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={parentVisible}
                        onChange={() => handleMenuToggle(parent.key, childKeys)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-900" />
                    </label>
                  </div>
                  {childKeys.length > 0 && (
                    <div className="divide-y divide-gray-100 bg-white">
                      {parent.submenu.map((sub) => {
                        const subVisible = menuVisibility[sub.key] !== false;
                        return (
                          <div
                            key={sub.key}
                            className="flex items-center justify-between py-3 px-4 pl-8 hover:bg-gray-50 transition"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-700">{sub.label}</p>
                              <p className="text-xs text-gray-400">{subVisible ? 'Visible' : 'Hidden'}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={subVisible}
                                onChange={() => handleMenuToggle(sub.key)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-900" />
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button
            onClick={handleSaveMenu}
            className="w-full mt-6 bg-gradient-to-r from-purple-900 to-purple-800 text-white py-3 rounded-lg hover:from-purple-800 hover:to-purple-700 transition font-semibold flex items-center justify-center gap-2"
          >
            <FaSave /> Save Menu Settings
          </button>
        </motion.div>

        {/* School Information */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-orange-100 p-3 rounded-full">
              <FaMapMarkerAlt className="text-2xl text-orange-900" />
            </div>
            <h2 className="text-2xl font-bold text-blue-900">School Information</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">School Name</label>
              <input
                type="text"
                name="SCHOOL_NAME"
                value={schoolInfo.SCHOOL_NAME}
                onChange={handleSchoolInfoChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tagline (header / footer)</label>
              <input
                type="text"
                name="SCHOOL_TAGLINE"
                value={schoolInfo.SCHOOL_TAGLINE}
                onChange={handleSchoolInfoChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">{getSchoolInfoDescription('SCHOOL_TAGLINE')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Home hero — extra line</label>
              <input
                type="text"
                name="SCHOOL_HERO_SUBLINE"
                value={schoolInfo.SCHOOL_HERO_SUBLINE}
                onChange={handleSchoolInfoChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo URL or path</label>
              <input
                type="text"
                name="SCHOOL_LOGO_URL"
                value={schoolInfo.SCHOOL_LOGO_URL}
                onChange={handleSchoolInfoChange}
                placeholder="/images/logo.png or https://..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Public folder path ya uploaded file ka URL (php_backend/uploads/...)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
              <input
                type="tel"
                name="SCHOOL_PHONE"
                value={schoolInfo.SCHOOL_PHONE}
                onChange={handleSchoolInfoChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Alternate Contact Number</label>
              <input
                type="tel"
                name="SCHOOL_PHONE_ALT"
                value={schoolInfo.SCHOOL_PHONE_ALT}
                onChange={handleSchoolInfoChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">{getSchoolInfoDescription('SCHOOL_PHONE_ALT')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Landline Number</label>
              <input
                type="text"
                name="SCHOOL_LANDLINE"
                value={schoolInfo.SCHOOL_LANDLINE}
                onChange={handleSchoolInfoChange}
                placeholder="0180-4017777"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">{getSchoolInfoDescription('SCHOOL_LANDLINE')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                name="SCHOOL_EMAIL_INFO"
                value={schoolInfo.SCHOOL_EMAIL_INFO}
                onChange={handleSchoolInfoChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Admissions Session Label</label>
              <input
                type="text"
                name="ADMISSIONS_SESSION_LABEL"
                value={schoolInfo.ADMISSIONS_SESSION_LABEL}
                onChange={handleSchoolInfoChange}
                placeholder="2026-27 (blank = auto)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">{getSchoolInfoDescription('ADMISSIONS_SESSION_LABEL')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">School Address</label>
              <textarea
                name="SCHOOL_ADDRESS"
                value={schoolInfo.SCHOOL_ADDRESS}
                onChange={handleSchoolInfoChange}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
            </div>

            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-lg font-bold text-blue-900 mb-2">Leadership Message (Dynamic)</h3>
              <p className="text-sm text-gray-600 mb-4">
                Leadership members ko yahan se add/remove karein. Zarurat ho to add karein, nahi ho to remove karke section empty kar sakte ho.
              </p>

              <div className="flex items-center justify-between p-4 mb-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <p className="font-semibold text-gray-800">Show Leadership Message</p>
                  <p className="text-xs text-gray-500">{schoolInfo.SHOW_LEADERSHIP_MESSAGE ? 'Visible' : 'Hidden'}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!schoolInfo.SHOW_LEADERSHIP_MESSAGE}
                    onChange={(e) =>
                      setSchoolInfo((prev) => ({
                        ...prev,
                        SHOW_LEADERSHIP_MESSAGE: e.target.checked,
                      }))
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-900" />
                </label>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-md font-bold text-blue-900 mb-2">Leadership Members (Advanced)</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Yahan se aap 2, 3, 4 (ya zyada) leaders add/remove kar sakte ho, designation change kar sakte ho, aur Home/About par alag-alag show/hide kar sakte ho.
                </p>

                <div className="space-y-4">
                  {(Array.isArray(schoolInfo.LEADERSHIP_MEMBERS) ? schoolInfo.LEADERSHIP_MEMBERS : []).map((m, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                          <input
                            type="text"
                            value={m?.name || ''}
                            onChange={(e) =>
                              setSchoolInfo((prev) => {
                                const list = Array.isArray(prev.LEADERSHIP_MEMBERS) ? [...prev.LEADERSHIP_MEMBERS] : [];
                                list[idx] = { ...(list[idx] || {}), name: e.target.value };
                                return { ...prev, LEADERSHIP_MEMBERS: list };
                              })
                            }
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
                          <input
                            type="text"
                            value={m?.designation || ''}
                            onChange={(e) =>
                              setSchoolInfo((prev) => {
                                const list = Array.isArray(prev.LEADERSHIP_MEMBERS) ? [...prev.LEADERSHIP_MEMBERS] : [];
                                list[idx] = { ...(list[idx] || {}), designation: e.target.value };
                                return { ...prev, LEADERSHIP_MEMBERS: list };
                              })
                            }
                            placeholder="Director / Principal / Manager / ..."
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Image URL/Path</label>
                          <input
                            type="text"
                            value={m?.imageUrl || ''}
                            onChange={(e) =>
                              setSchoolInfo((prev) => {
                                const list = Array.isArray(prev.LEADERSHIP_MEMBERS) ? [...prev.LEADERSHIP_MEMBERS] : [];
                                list[idx] = { ...(list[idx] || {}), imageUrl: e.target.value };
                                return { ...prev, LEADERSHIP_MEMBERS: list };
                              })
                            }
                            placeholder="/images/... or uploads/..."
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                          />
                          <div className="mt-2 flex items-center gap-3">
                            <label className="text-xs font-semibold text-gray-600">
                              Upload Image
                              <input
                                type="file"
                                accept="image/*"
                                className="block mt-1 text-xs text-gray-600"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const reader = new FileReader();
                                  reader.onload = () => {
                                    const result = String(reader.result || '');
                                    setSchoolInfo((prev) => {
                                      const list = Array.isArray(prev.LEADERSHIP_MEMBERS) ? [...prev.LEADERSHIP_MEMBERS] : [];
                                      list[idx] = { ...(list[idx] || {}), imageUrl: result };
                                      return { ...prev, LEADERSHIP_MEMBERS: list };
                                    });
                                  };
                                  reader.readAsDataURL(file);
                                }}
                              />
                            </label>
                            {!!m?.imageUrl && (
                              <img
                                src={m.imageUrl}
                                alt="preview"
                                className="w-12 h-12 rounded-lg object-cover border border-gray-200 bg-white"
                              />
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                              type="checkbox"
                              checked={m?.isActive !== false}
                              onChange={(e) =>
                                setSchoolInfo((prev) => {
                                  const list = Array.isArray(prev.LEADERSHIP_MEMBERS) ? [...prev.LEADERSHIP_MEMBERS] : [];
                                  list[idx] = { ...(list[idx] || {}), isActive: e.target.checked };
                                  return { ...prev, LEADERSHIP_MEMBERS: list };
                                })
                              }
                            />
                            Active
                          </label>
                          <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                              type="checkbox"
                              checked={m?.showOnHome !== false}
                              onChange={(e) =>
                                setSchoolInfo((prev) => {
                                  const list = Array.isArray(prev.LEADERSHIP_MEMBERS) ? [...prev.LEADERSHIP_MEMBERS] : [];
                                  list[idx] = { ...(list[idx] || {}), showOnHome: e.target.checked };
                                  return { ...prev, LEADERSHIP_MEMBERS: list };
                                })
                              }
                            />
                            Show on Home
                          </label>
                          <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                              type="checkbox"
                              checked={m?.showOnAbout !== false}
                              onChange={(e) =>
                                setSchoolInfo((prev) => {
                                  const list = Array.isArray(prev.LEADERSHIP_MEMBERS) ? [...prev.LEADERSHIP_MEMBERS] : [];
                                  list[idx] = { ...(list[idx] || {}), showOnAbout: e.target.checked };
                                  return { ...prev, LEADERSHIP_MEMBERS: list };
                                })
                              }
                            />
                            Show on About
                          </label>
                        </div>
                      </div>

                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                        <textarea
                          value={m?.message || ''}
                          onChange={(e) =>
                            setSchoolInfo((prev) => {
                              const list = Array.isArray(prev.LEADERSHIP_MEMBERS) ? [...prev.LEADERSHIP_MEMBERS] : [];
                              list[idx] = { ...(list[idx] || {}), message: e.target.value };
                              return { ...prev, LEADERSHIP_MEMBERS: list };
                            })
                          }
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                        />
                      </div>

                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          onClick={() =>
                            setSchoolInfo((prev) => {
                              const list = Array.isArray(prev.LEADERSHIP_MEMBERS) ? [...prev.LEADERSHIP_MEMBERS] : [];
                              list.splice(idx, 1);
                              return { ...prev, LEADERSHIP_MEMBERS: list };
                            })
                          }
                          className="px-4 py-2 rounded-lg bg-red-50 text-red-700 font-semibold hover:bg-red-100 transition"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setSchoolInfo((prev) => {
                      const list = Array.isArray(prev.LEADERSHIP_MEMBERS) ? [...prev.LEADERSHIP_MEMBERS] : [];
                      list.push({
                        name: '',
                        designation: '',
                        message: '',
                        imageUrl: '',
                        isActive: true,
                        showOnHome: true,
                        showOnAbout: true,
                      });
                      return { ...prev, LEADERSHIP_MEMBERS: list };
                    })
                  }
                  className="mt-4 w-full px-4 py-3 rounded-xl bg-blue-50 text-blue-900 font-semibold hover:bg-blue-100 transition"
                >
                  + Add Member
                </button>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <h4 className="text-md font-bold text-blue-900 mb-2">Home Page — Achievements & Recognition</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Home page par “Our Achievements & Recognition” section yahan se dynamic manage hoga.
                </p>

                <div className="flex items-center justify-between p-4 mb-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <p className="font-semibold text-gray-800">Show Achievements Section</p>
                    <p className="text-xs text-gray-500">
                      {schoolInfo.SHOW_ACHIEVEMENTS_SECTION ? 'Visible' : 'Hidden'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!schoolInfo.SHOW_ACHIEVEMENTS_SECTION}
                      onChange={(e) =>
                        setSchoolInfo((prev) => ({
                          ...prev,
                          SHOW_ACHIEVEMENTS_SECTION: e.target.checked,
                        }))
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-900" />
                  </label>
                </div>

                <div className="space-y-4">
                  {(Array.isArray(schoolInfo.ACHIEVEMENTS_ITEMS) ? schoolInfo.ACHIEVEMENTS_ITEMS : []).map((a, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Metric</label>
                          <input
                            type="text"
                            value={a?.metric || ''}
                            onChange={(e) =>
                              setSchoolInfo((prev) => {
                                const list = Array.isArray(prev.ACHIEVEMENTS_ITEMS) ? [...prev.ACHIEVEMENTS_ITEMS] : [];
                                list[idx] = { ...(list[idx] || {}), metric: e.target.value };
                                return { ...prev, ACHIEVEMENTS_ITEMS: list };
                              })
                            }
                            placeholder="e.g. 98% / 25+ / 1st Rank"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                          <input
                            type="text"
                            value={a?.title || ''}
                            onChange={(e) =>
                              setSchoolInfo((prev) => {
                                const list = Array.isArray(prev.ACHIEVEMENTS_ITEMS) ? [...prev.ACHIEVEMENTS_ITEMS] : [];
                                list[idx] = { ...(list[idx] || {}), title: e.target.value };
                                return { ...prev, ACHIEVEMENTS_ITEMS: list };
                              })
                            }
                            placeholder="e.g. Board Results"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Icon (emoji)</label>
                          <input
                            type="text"
                            value={a?.icon || ''}
                            onChange={(e) =>
                              setSchoolInfo((prev) => {
                                const list = Array.isArray(prev.ACHIEVEMENTS_ITEMS) ? [...prev.ACHIEVEMENTS_ITEMS] : [];
                                list[idx] = { ...(list[idx] || {}), icon: e.target.value };
                                return { ...prev, ACHIEVEMENTS_ITEMS: list };
                              })
                            }
                            placeholder="🏆"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                        <textarea
                          value={a?.subtitle || ''}
                          onChange={(e) =>
                            setSchoolInfo((prev) => {
                              const list = Array.isArray(prev.ACHIEVEMENTS_ITEMS) ? [...prev.ACHIEVEMENTS_ITEMS] : [];
                              list[idx] = { ...(list[idx] || {}), subtitle: e.target.value };
                              return { ...prev, ACHIEVEMENTS_ITEMS: list };
                            })
                          }
                          rows={2}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                          placeholder="Short description"
                        />
                      </div>

                      <div className="mt-4 flex items-center justify-between">
                        <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                          <input
                            type="checkbox"
                            checked={a?.isActive !== false}
                            onChange={(e) =>
                              setSchoolInfo((prev) => {
                                const list = Array.isArray(prev.ACHIEVEMENTS_ITEMS) ? [...prev.ACHIEVEMENTS_ITEMS] : [];
                                list[idx] = { ...(list[idx] || {}), isActive: e.target.checked };
                                return { ...prev, ACHIEVEMENTS_ITEMS: list };
                              })
                            }
                          />
                          Active
                        </label>

                        <button
                          type="button"
                          onClick={() =>
                            setSchoolInfo((prev) => {
                              const list = Array.isArray(prev.ACHIEVEMENTS_ITEMS) ? [...prev.ACHIEVEMENTS_ITEMS] : [];
                              list.splice(idx, 1);
                              return { ...prev, ACHIEVEMENTS_ITEMS: list };
                            })
                          }
                          className="px-4 py-2 rounded-lg bg-red-50 text-red-700 font-semibold hover:bg-red-100 transition"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setSchoolInfo((prev) => {
                      const list = Array.isArray(prev.ACHIEVEMENTS_ITEMS) ? [...prev.ACHIEVEMENTS_ITEMS] : [];
                      list.push({ metric: '', title: '', subtitle: '', icon: '🏆', isActive: true });
                      return { ...prev, ACHIEVEMENTS_ITEMS: list };
                    })
                  }
                  className="mt-4 w-full px-4 py-3 rounded-xl bg-blue-50 text-blue-900 font-semibold hover:bg-blue-100 transition"
                >
                  + Add Achievement
                </button>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <h4 className="text-md font-bold text-blue-900 mb-2">Home Page — Stats Counters</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Home page par jo counters (Students, Teachers, Years Experience, Result Focus) dikhte hain — unki value yahan se change/hide kar sakte ho.
                </p>

                <div className="flex items-center justify-between p-4 mb-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <p className="font-semibold text-gray-800">Show Stats Counters</p>
                    <p className="text-xs text-gray-500">{schoolInfo.SHOW_HOME_STATS ? 'Visible' : 'Hidden'}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!schoolInfo.SHOW_HOME_STATS}
                      onChange={(e) =>
                        setSchoolInfo((prev) => ({
                          ...prev,
                          SHOW_HOME_STATS: e.target.checked,
                        }))
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-900" />
                  </label>
                </div>

                <div className="space-y-3">
                  {(Array.isArray(schoolInfo.HOME_STATS_ITEMS) ? schoolInfo.HOME_STATS_ITEMS : []).map((s, idx) => (
                    <div key={s?.key || idx} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                          <input
                            type="text"
                            value={s?.label || ''}
                            onChange={(e) =>
                              setSchoolInfo((prev) => {
                                const list = Array.isArray(prev.HOME_STATS_ITEMS) ? [...prev.HOME_STATS_ITEMS] : [];
                                list[idx] = { ...(list[idx] || {}), label: e.target.value };
                                return { ...prev, HOME_STATS_ITEMS: list };
                              })
                            }
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Number</label>
                          <input
                            type="number"
                            value={Number.isFinite(Number(s?.number)) ? Number(s.number) : 0}
                            onChange={(e) =>
                              setSchoolInfo((prev) => {
                                const list = Array.isArray(prev.HOME_STATS_ITEMS) ? [...prev.HOME_STATS_ITEMS] : [];
                                list[idx] = { ...(list[idx] || {}), number: Number(e.target.value || 0) };
                                return { ...prev, HOME_STATS_ITEMS: list };
                              })
                            }
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Suffix</label>
                          <input
                            type="text"
                            value={s?.suffix || ''}
                            onChange={(e) =>
                              setSchoolInfo((prev) => {
                                const list = Array.isArray(prev.HOME_STATS_ITEMS) ? [...prev.HOME_STATS_ITEMS] : [];
                                list[idx] = { ...(list[idx] || {}), suffix: e.target.value };
                                return { ...prev, HOME_STATS_ITEMS: list };
                              })
                            }
                            placeholder="+ / %"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                          />
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                              type="checkbox"
                              checked={s?.isActive !== false}
                              onChange={(e) =>
                                setSchoolInfo((prev) => {
                                  const list = Array.isArray(prev.HOME_STATS_ITEMS) ? [...prev.HOME_STATS_ITEMS] : [];
                                  list[idx] = { ...(list[idx] || {}), isActive: e.target.checked };
                                  return { ...prev, HOME_STATS_ITEMS: list };
                                })
                              }
                            />
                            Active
                          </label>
                          <span className="text-xs text-gray-400">Key: {s?.key}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <h3 className="text-lg font-bold text-blue-900 mb-2">Google Map (Dynamic)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Google Maps → Share → Embed a map → iframe me jo <strong>src</strong> URL hota hai, wahi yahan paste karein.
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-2">Google Map Embed URL</label>
                <input
                  type="url"
                  name="GOOGLE_MAP_EMBED_URL"
                  value={schoolInfo.GOOGLE_MAP_EMBED_URL}
                  onChange={handleSchoolInfoChange}
                  placeholder="https://www.google.com/maps/embed?pb=..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">{getSchoolInfoDescription('GOOGLE_MAP_EMBED_URL')}</p>
              </div>
            </div>

            <button
              onClick={handleSaveSchoolInfo}
              className="w-full bg-gradient-to-r from-orange-900 to-orange-800 text-white py-3 rounded-lg hover:from-orange-800 hover:to-orange-700 transition font-semibold flex items-center justify-center gap-2"
            >
              <FaSave /> Save School Information
            </button>
          </div>
        </motion.div>

        {/* Social Media Links */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-pink-100 p-3 rounded-full">
              <FaFacebookF className="text-2xl text-pink-900" />
            </div>
            <h2 className="text-2xl font-bold text-blue-900">Social Media Links</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaFacebookF className="inline mr-2" /> Facebook URL
              </label>
              <input
                type="url"
                name="SOCIAL_FACEBOOK"
                value={socialLinks.SOCIAL_FACEBOOK}
                onChange={handleSocialChange}
                placeholder="https://www.facebook.com/your-page"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaInstagram className="inline mr-2" /> Instagram URL
              </label>
              <input
                type="url"
                name="SOCIAL_INSTAGRAM"
                value={socialLinks.SOCIAL_INSTAGRAM}
                onChange={handleSocialChange}
                placeholder="https://www.instagram.com/your-profile"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaTwitter className="inline mr-2" /> Twitter/X URL
              </label>
              <input
                type="url"
                name="SOCIAL_TWITTER"
                value={socialLinks.SOCIAL_TWITTER}
                onChange={handleSocialChange}
                placeholder="https://x.com/your-profile"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaYoutube className="inline mr-2" /> YouTube URL
              </label>
              <input
                type="url"
                name="SOCIAL_YOUTUBE"
                value={socialLinks.SOCIAL_YOUTUBE}
                onChange={handleSocialChange}
                placeholder="https://www.youtube.com/your-channel"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              />
            </div>

            <button
              onClick={handleSaveSocial}
              className="w-full bg-gradient-to-r from-pink-900 to-pink-800 text-white py-3 rounded-lg hover:from-pink-800 hover:to-pink-700 transition font-semibold flex items-center justify-center gap-2"
            >
              <FaSave /> Save Social Media Links
            </button>
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
}

export default Settings;
